import React, { useEffect, useState } from "react";
import { Star, Plus, ArrowSquareOut } from "@phosphor-icons/react";
import { API, http } from "@/lib/constants";
import { cn, formatRelativeTime } from "@/lib/format";
import { useWatchlist } from "@/components/assets/watchlist";
import { Panel, TokenLogo, MiniSpark, VerifiedTick, assetLabel, fmtQtyMeta, fmtInt } from "@/components/assets/parts";
import { useTokenMeta, useRequestMeta } from "@/components/assets/tokenMeta";
import { useAssetDailySeries } from "@/components/assets/assetSeries";

const summaryCache = new Map();
const fetchSummary = (asset, bust) => {
  if (bust) summaryCache.delete(asset);
  if (!summaryCache.has(asset)) {
    summaryCache.set(asset, http.get(`${API}/assets/${asset}`, { timeout: 20000 }).then((r) => r.data).catch(() => null));
  }
  return summaryCache.get(asset);
};

const WatchRow = ({ item, onPickAsset, onRemove, tick }) => {
  const [s, setS] = useState(null);
  const { meta } = useTokenMeta();
  useEffect(() => {
    let m = true;
    fetchSummary(item.asset, tick > 0).then((d) => { if (m) setS(d); });
    return () => { m = false; };
  }, [item.asset, tick]);
  const reg = meta.get(item.asset);
  const series = useAssetDailySeries(item.asset, true);
  const { text } = assetLabel(item);
  const display = reg?.name || reg?.ticker || text;
  return (
    <div data-testid="watch-row" className="group flex items-center gap-3 px-4 py-2.5 border-b border-primary/5 last:border-b-0 hover:bg-primary/5 transition-colors">
      <button type="button" data-testid="watch-remove" onClick={() => onRemove(item.asset)} title="Remove from watchlist" aria-label="Remove from watchlist" className="shrink-0 text-[#22d3ee] hover:text-rose-400 transition-colors">
        <Star size={15} weight="fill" />
      </button>
      <TokenLogo item={item} size={30} />
      <button type="button" data-testid="watch-asset" onClick={() => onPickAsset?.(item.asset)} className="min-w-0 flex-1 text-left">
        <div className="truncate text-[13px] mono text-foreground inline-flex items-center gap-1 group-hover:text-[#22d3ee] transition-colors">
          <span className="truncate">{display}</span>
          {reg?.name ? <VerifiedTick /> : null}
          <ArrowSquareOut size={11} className="shrink-0 opacity-0 group-hover:opacity-70" />
        </div>
        <div className="truncate text-[10px] mono text-muted-foreground/70">{item.fingerprint ? `${item.fingerprint.slice(0, 14)}…` : item.asset.slice(0, 14)}</div>
      </button>
      {Array.isArray(series) ? (
        <span className="shrink-0 hidden sm:inline-flex" title="Daily transactions, last 14 days" data-testid="watch-spark">
          <MiniSpark values={series} color="#22d3ee" width={52} height={22} />
        </span>
      ) : null}
      <div className="shrink-0 text-right">
        <div className="text-[12px] mono tabnum text-foreground/90">{s ? fmtQtyMeta(s.quantity, reg) : "…"}</div>
        <div className="text-[9px] uppercase tracking-[0.1em] mono text-muted-foreground/60">{s ? `${fmtInt(s.mint_or_burn_count)} m/b` : "supply"}</div>
      </div>
    </div>
  );
};

export const Watchlist = ({ onPickAsset, refreshMs = 90000 }) => {
  const { list, remove } = useWatchlist();
  const [tick, setTick] = useState(0);
  useRequestMeta(list.map((it) => it.asset));

  useEffect(() => {
    if (!list.length) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), refreshMs);
    return () => clearInterval(id);
  }, [list.length, refreshMs]);

  const addBtn = (
    <button
      type="button"
      data-testid="watchlist-add"
      onClick={() => window.dispatchEvent(new Event("ga-focus-asset-search"))}
      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-[11px] uppercase tracking-[0.18em] mono text-primary border border-primary/25 rounded-xl bg-primary/5 hover:bg-primary/15 transition-colors"
    >
      <Plus size={13} weight="bold" /> Add asset to watchlist
    </button>
  );

  return (
    <Panel
      title="Watchlist"
      icon={Star}
      right={list.length ? <span className="text-[9px] uppercase tracking-[0.12em] text-muted-foreground/60 mono">{list.length}/12 pinned</span> : null}
      testid="watchlist"
    >
      {!list.length ? (
        <div className="px-5 py-8 text-center space-y-4">
          <div className="text-[12px] text-muted-foreground mono leading-relaxed">Pin assets with the <Star size={11} weight="fill" className="inline text-[#22d3ee] -mt-0.5" /> icon from any table to track their supply &amp; mint/burn activity here.</div>
          {addBtn}
        </div>
      ) : (
        <>
          <div className={cn("overflow-y-auto", list.length > 6 && "max-h-[360px]")}>
            {list.map((it) => (
              <WatchRow key={it.asset} item={it} onPickAsset={onPickAsset} onRemove={remove} tick={tick} />
            ))}
          </div>
          <div className="p-3 border-t border-primary/10">{addBtn}</div>
        </>
      )}
    </Panel>
  );
};
